
import { IngestFragment, Label, SemanticSearchResult } from '@/model';
import Vue from 'vue';
import DocumentHeader from '@/components/DocumentHeader.vue';
import LabeledTextWithContext from '@/components/LabeledTextWithContext.vue';
import LabelPanel from '@/components/LabelPanel.vue';
import HtmlTable from '@/components/HtmlTable.vue';
import { distinct, notEmpty } from '@/util/array-util';
import labels, { labelSorter } from '@/store/labels';

export default Vue.extend({
  name: 'RelatedFragments',
  components: {
    DocumentHeader,
    LabeledTextWithContext,
    LabelPanel,
    HtmlTable,
  },
  props: {
    value: { type: Array as () => SemanticSearchResult[], required: true },
    edit: { type: Boolean, default: false },
    documentId: { type: Number as () => number },
    disabledLabels: { type: Array as () => number[], default: () => [] },
  },

  data() {
    return {
      relatedFragments: [] as SemanticSearchResult[],
      excludeLabelTypes: ['Workflow'] as string[],
    };
  },

  watch: {
    value: {
      immediate: true,
      handler(value) {
        this.relatedFragments = value;
      },
    },
  },
  methods: {
    isHtmlTable(text: string): boolean {
      return !!text && text.trimStart().toLowerCase().startsWith('<table');
    },
    fragmnetLabels(fragments: IngestFragment[]): Label[] {
      const allLabels = distinct(fragments.flatMap((f) => f.labels).map((l) => l.labelId));
      return allLabels
        .map((id) => labels.getters.lookup.get(id))
        .filter(notEmpty)
        .filter((label) => !this.excludeLabelTypes.includes(label.type))
        .sort(labelSorter(['type', 'length', 'alpha']));
    },
    labelText(label: Label): string {
      return label.code;
    },
    labelStyle(label: Label): object {
      const color = this.labelColor(label);
      return {
        'background-color': color,
        'border-color': color,
      };
    },
    labelClick(label: Label) {
      this.$emit('labelClick', label);
    },
    labelColor(label: Label) {
      return `rgba(${label.color.r}, ${label.color.g}, ${label.color.b}, 1)`;
    },

    stars(score: number): number {
      // lower is better, lower than 0.1 is 3
      if (score < 0.1) {
        return 3;
      }
      if (score < 0.2) {
        return 2;
      }
      if (score < 0.3) {
        return 1;
      }
      return 0;
    },

    rankLabels(results: SemanticSearchResult[]): Label[] {
      // use result as knn and rank labels by their score, use threshold of 0.5'

      const relevantResults = results; // .filter((r) => r.item.score < 0.35);

      // frequency of labels
      const counter = new Map<number, number>();
      const scoreCounter = new Map<number, number>();
      relevantResults.forEach((result) => {
        const ls = this.fragmnetLabels(result.fragments);
        ls.forEach((label) => {
          const count = counter.get(label.id) ?? 0;
          counter.set(label.id, count + 1);
          const current = scoreCounter.get(label.id) ?? 0;
          const score = 1 - (result.item.score ?? 0);
          scoreCounter.set(label.id, current + score);
        });
      });

      const maxScore = relevantResults.map((r) => 1 - r.item.score).reduce((a, b) => a + b, 0);
      // use max score to normalize
      const normalizedScoreCounter = new Map<number, number>();
      scoreCounter.forEach((score, id) => {
        normalizedScoreCounter.set(id, score / maxScore);
      });

      const ls = [...normalizedScoreCounter.entries()]
        .filter(([_, count]) => count >= 0.40)
        .map(([id, _]) => labels.getters.lookup.get(id))
        .filter(notEmpty)
        .sort(labelSorter(['type', 'length', 'alpha']))
        .filter((l) => l.type !== 'Workflow');
      return ls;
    },
    score(result: SemanticSearchResult): string {
      // format score as percentage
      return `${(100 * (1 - result.item.score)).toFixed(1)}%`;
    },
  },

});
